import { memo } from 'react'
import cn from 'classnames'

import { Icon } from '~/shared/ui/Icon'
import { IconButton } from '~/shared/ui/IconButton'
import { Text } from '~/shared/ui/Text'
import { VStack } from '~/shared/ui/VStack'

import {
  NOTIFICATION_BADGE_ICON_SIZE,
  NOTIFICATION_CLOSE_ICON_SIZE,
  NOTIFICATION_CLOSE_LABEL,
  NOTIFICATION_CLOSE_SIZE,
  NOTIFICATION_THEME_ICON,
} from './config'
import type { NotificationProps } from './types'

import styles from './Notification.module.scss'

/**
 * Презентационная карточка одного уведомления. Не знает про стек, таймер и жесты закрытия —
 * этим управляет NotificationList/AnimatedNotification.
 *
 * Выглядит как обычная карточка продукта: та же поверхность `surface`, тот же радиус и тень.
 * Тип несут только иконка-бейдж и тонкая обводка его цветом — по той же логике, что у
 * «главного» блока на экранах результата. До этого фон заливался цветом типа целиком, и
 * уведомление выпадало из языка интерфейса: в продукте нет ни одной цветной карточки.
 *
 * Высота идёт по содержимому: одна строка, если есть только заголовок, и две — когда под ним
 * появляется текст. Ничего не резервируется заранее.
 */
export const Notification = memo(function Notification(props: NotificationProps) {
  const { theme, title, text, onClose } = props

  return (
    <div className={cn(styles.root, styles[`theme_${theme}`])} role='status'>
      <div className={styles.badge}>
        <Icon
          name={NOTIFICATION_THEME_ICON[theme]}
          size={NOTIFICATION_BADGE_ICON_SIZE}
          strokeWidth={1.7}
        />
      </div>

      <div className={styles.body}>
        <VStack gap='SMALL_XXS'>
          {title && (
            <Text variant='H3' color='PRIMARY'>
              {title}
            </Text>
          )}
          <Text variant='BODY_S' color={title ? 'SECONDARY' : 'PRIMARY'}>
            {text}
          </Text>
        </VStack>
      </div>

      <IconButton
        iconName='close'
        size={NOTIFICATION_CLOSE_SIZE}
        iconSize={NOTIFICATION_CLOSE_ICON_SIZE}
        shape='CIRCLE'
        variant='GHOST'
        ariaLabel={NOTIFICATION_CLOSE_LABEL}
        onPress={onClose}
      />
    </div>
  )
})
